


import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  Box, Grid, Card, CardContent, Typography, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Select, MenuItem, FormControl, InputLabel,
  Button, IconButton, Modal, Fade, Backdrop, CircularProgress,
  Stack, Alert, InputAdornment,
  useMediaQuery, useTheme, Avatar, Tooltip, Dialog, DialogTitle,
  DialogContent,ToggleButtonGroup,ToggleButton
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  TableRows as TableRowsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as EyeIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { apiRequest } from "../../fetchSetup";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { registerFetchedOrders, isOrderUnseen, markOrderSeen } from '../../utils/orderNotifications';
// ─── Theme ──────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary:    { main: "#006D77", light: "#4A9FA8", dark: "#003D45" },
    secondary:  { main: "#4A9FA8" },
    background: { default: "#F0F4F8", paper: "#FFFFFF" },
    success:    { main: "#16A34A" },
    warning:    { main: "#D97706" },
    error:      { main: "#DC2626" },
    info:       { main: "#007A85" },
  },
  typography: {
    fontFamily: "'DM Sans', sans-serif",
    h4: { fontFamily: "'Sora', sans-serif", fontWeight: 900 },
    h5: { fontFamily: "'Sora', sans-serif", fontWeight: 800 },
    h6: { fontFamily: "'Sora', sans-serif", fontWeight: 800 },
    subtitle1: { fontWeight: 700 },
    button: { fontFamily: "'DM Sans', sans-serif", fontWeight: 700 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCard: { styleOverrides: { root: { borderRadius: 18, boxShadow: "0 4px 20px rgba(0,0,0,0.07)" } } },
    MuiButton: { styleOverrides: { root: { borderRadius: 12, textTransform: "none", fontWeight: 700, fontSize: 13 } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 700, fontSize: 11, borderRadius: 20 } } },
    MuiTableCell: {
      styleOverrides: {
        head: { fontWeight: 800, fontSize: 11, letterSpacing: "0.5px", textTransform: "uppercase", color: "#006D77", backgroundColor: "#F0F9F9" },
        body: { fontSize: 13 },
      },
    },
    MuiTableRow: { styleOverrides: { root: { "&:hover": { backgroundColor: "#F0F9F9" }, cursor: "pointer" } } },
  },
});

// ─── API ────────────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API;
const UPLOAD_PKG_DOC_API   = process.env.REACT_APP_UPLOAD_PKG_BOOKING_DOC;
const DOWNLOAD_PKG_DOC_API = process.env.REACT_APP_UPLOAD_PKG_BOOKING_DOC;
const DELETE_PKG_DOC_API   = process.env.REACT_APP_UPLOAD_PKG_BOOKING_DOC;

const getAuthHeader = () => {
  const token = sessionStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ROWS_PER_PAGE = 8;

const STATUS_CHIP = {
  Pending:   { color: "warning", variant: "filled" },
  Confirmed: { color: "info",    variant: "filled" },
  Collected: { color: "secondary", variant: "filled" },
  Completed: { color: "success", variant: "filled" },
  Cancelled: { color: "error",   variant: "filled" },
};
const PAYMENT_CHIP = {
  Unpaid:   { color: "warning", variant: "outlined" },
  Paid:     { color: "success", variant: "filled"   },
  Refunded: { color: "default", variant: "outlined" },
};

// ─── File helpers — mirrors booking_services.py storage_type logic ──────────────
const getFileIcon = (fileName) => {
  if (!fileName || typeof fileName !== "string") return <FileIcon sx={{ fontSize: 40, color: "#6B7280" }} />;
  const ext = fileName.split(".").pop().toLowerCase();
  switch (ext) {
    case "pdf":  return <PdfIcon         sx={{ fontSize: 40, color: "#EF4444" }} />;
    case "png":
    case "jpg":
    case "jpeg": return <ImageIcon       sx={{ fontSize: 40, color: "#3B82F6" }} />;
    case "doc":
    case "docx": return <DescriptionIcon sx={{ fontSize: 40, color: "#2563EB" }} />;
    default:     return <FileIcon        sx={{ fontSize: 40, color: "#6B7280" }} />;
  }
};

const isImageFile = (filename = "") =>
  ["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(filename.split(".").pop().toLowerCase());

const isPdfFile = (filename = "") => filename.split(".").pop().toLowerCase() === "pdf";

// ─── StatusChip ─────────────────────────────────────────────────────────────────
const StatusChip = ({ value, type = "status" }) => {
  const map = type === "payment" ? PAYMENT_CHIP : STATUS_CHIP;
  const c   = map[value] || { color: "default", variant: "outlined" };
  return <Chip label={value} color={c.color} variant={c.variant} size="small" />;
};


// ─── BookingDetailModal ──────────────────────────────────────────────────────────
const BookingDetailModal = ({ booking, onClose, onUpdate, pkgMeta }) => {
  const [status,    setStatus]    = useState(booking.status);
  const [payStatus, setPayStatus] = useState(booking.payment_status);
  const [notes,     setNotes]     = useState(booking.notes || "");
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState(null);

  // Document states
  const [selectedFile,      setSelectedFile]     = useState([]);
  const [filePreviewUrl,    setFilePreviewUrl]    = useState([]);
  const [uploading,         setUploading]         = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFile,       setPreviewFile]       = useState(null);
  const [previewContent,    setPreviewContent]    = useState(null);

  const [localBooking, setLocalBooking] = useState(booking);
  const [txnId, setTxnId] = useState(booking.transaction_id || (booking.payment_method === "online" ? booking.razorpay_payment_id || "" : ""));
  const [amountPaid, setAmountPaid] = useState(booking.amount_paid || (booking.payment_status === "Paid" ? booking.offer_price || "" : ""));
  const [payDate, setPayDate] = useState(booking.payment_date ||(booking.payment_status === "Paid" && booking.created_at? booking.created_at.split(" ")[0]: ""));
  const [payGateway, setPayGateway] = useState( booking.payment_gateway || (booking.payment_method === "online" ? "Razorpay" : ""));
  const [txnRemarks,  setTxnRemarks]  = useState(booking.transaction_remarks || "");

  const modalTheme = useTheme();
  const fullScreen = useMediaQuery(modalTheme.breakpoints.down("sm"));
  const meta = pkgMeta[booking.package_id] || { gradient: "linear-gradient(135deg,#006D77,#003D45)", icon: "📦" };

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      filePreviewUrl.forEach((u) => u && URL.revokeObjectURL(u));
      if (previewContent?.url) URL.revokeObjectURL(previewContent.url);
    };
  }, [filePreviewUrl, previewContent]);

  const refreshBooking = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/package-orders/admin/${localBooking.id}`, { headers: getAuthHeader() });
      if (res.data.success && res.data.booking) {
        setLocalBooking(res.data.booking);
      }
    } catch (_) { /* silent */ }
    onUpdate();
  }, [localBooking.id, onUpdate]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = [
      "image/png", "image/jpeg", "image/jpg", "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const validFiles = [];
    const previews   = [];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setMsg({ type: "error", text: `${file.name} — unsupported format. Use PNG, JPG, PDF, DOC or DOCX.` });
        continue;
      }
      if (file.size > maxSize) {
        setMsg({ type: "error", text: `${file.name} exceeds 10 MB.` });
        continue;
      }
      validFiles.push(file);
      previews.push(file.type.startsWith("image/") ? URL.createObjectURL(file) : null);
    }
    setSelectedFile(validFiles);
    setFilePreviewUrl(previews);
    if (validFiles.length) setMsg(null);
    e.target.value = "";
  };

  const handleUploadDoc = async () => {
    if (!selectedFile.length || !localBooking) return;
    setUploading(true);
    const formData = new FormData();
    selectedFile.forEach((f) => formData.append("file", f));
    try {
      const response = await apiRequest(
        `${UPLOAD_PKG_DOC_API}/${localBooking.id}/documents`,
        { method: "POST", body: formData }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      const result = await response.json();
      setMsg({ type: "success", text: result.message || "Documents uploaded successfully!" });
      setSelectedFile([]);
      filePreviewUrl.forEach((u) => u && URL.revokeObjectURL(u));
      setFilePreviewUrl([]);
      await refreshBooking();
    } catch (err) {
      setMsg({ type: "error", text: "Failed to upload: " + err.message });
    } finally {
      setUploading(false);
    }
  };

  const handlePreviewDoc = async (doc) => {
    const filename = doc.filename;
    try {
      // S3: cloudfront_url is always set — open directly, same as DiagnosticOrdersScreen
      if (doc.cloudfront_url && doc.cloudfront_url.includes("cloudfront.net")) {
        window.open(doc.cloudfront_url, "_blank");
        return;
      }
      // Local storage or fallback: fetch presigned/blob from backend
      const response = await apiRequest(
        `${DOWNLOAD_PKG_DOC_API}/${localBooking.id}/documents/${filename}`,
        { method: "GET", credentials: "include", redirect: "manual" }
      );
      if (response.status === 302 || response.status === 307) {
        const redirectUrl = response.headers.get("Location");
        if (redirectUrl) { window.open(redirectUrl, "_blank"); return; }
      }
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
      const blob = await response.blob();
      if (!blob || blob.size === 0) throw new Error("Empty file received");
      const origName = doc.original_filename || filename;
      const blobUrl  = URL.createObjectURL(blob);
      setPreviewContent({ url: blobUrl, filename: origName, isImage: isImageFile(origName), isPdf: isPdfFile(origName) });
      setPreviewFile({ ...doc, bookingId: localBooking.id });
      setPreviewDialogOpen(true);
    } catch (err) {
      console.error("Preview error:", err);
      // Fallback to CloudFront direct (pkg_docs prefix matches backend S3_DIAGNOSTIC_REPORTS_PATH override)
      const fallbackUrl = `${process.env.REACT_APP_MEDIA_CDN || "https://dhlkl2wv8e0dt.cloudfront.net"}/pkg_docs/${localBooking.id}/${filename}`;
      window.open(fallbackUrl, "_blank");
    }
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    if (previewContent?.url) URL.revokeObjectURL(previewContent.url);
    setPreviewContent(null);
    setPreviewFile(null);
  };
     const handleInvoiceDownload=async(order_id)=>{
        try {
              const token = sessionStorage.getItem("token");
  
              const response = await axios.get(
                `${process.env.REACT_APP_API_INVOICE}/package-invoice/download/${order_id}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
              const download_url=response.data.download_url
              window.open(download_url, "_blank");
            } catch (error) {
              console.error("Invoice download failed:", error);
            }
  }
  

  const handleDownloadDoc = async (filename) => {
    try {
      const response = await apiRequest(
        `${DOWNLOAD_PKG_DOC_API}/${localBooking.id}/documents/${filename}`,
        { method: "GET" }
      );
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Download failed");
      }
      const contentType = response.headers.get("content-type") || "";

      // ── S3 path: backend returns JSON { download_url } 
      if (contentType.includes("application/json")) {
        const data = await response.json();
        if (data.download_url) {
          const a    = document.createElement("a");
          a.href     = data.download_url;
          a.target   = "_blank";          
          a.rel      = "noopener noreferrer";
          a.download = data.filename || filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
        throw new Error("No download_url in server response");
      }

      const blob    = await response.blob();
      if (!blob || blob.size === 0) throw new Error("Empty file received from server");
      const blobUrl = URL.createObjectURL(blob);
      const a       = document.createElement("a");
      a.href        = blobUrl;
      a.download    = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setMsg({ type: "error", text: "Failed to download: " + err.message });
    }
  };
  const formatAddress = (address) => {
  if (!address) return "";

  if (typeof address === "string") {
    return address;
  }

  return [
    address.address_line1,
    address.address_line2,
    address.landmark,
    address.city,
    address.state,
    address.pincode,
  ]
    .filter((value) => value && value.trim())
    .join(", ");
};

  // ── Delete — DELETE /package-orders/admin/<id>/documents/<filename> ───────────
  const handleDeleteDoc = async (filename) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      const response = await apiRequest(
        `${DELETE_PKG_DOC_API}/${localBooking.id}/documents/${filename}`,
        { method: "DELETE", headers: { Accept: "application/json", "Content-Type": "application/json" } }
      );
      const contentType = response.headers.get("content-type");
      if (!response.ok) {
        let errorMessage = `Server error: ${response.status} ${response.statusText}`;
        if (contentType && contentType.includes("application/json")) {
          try { const e = await response.json(); errorMessage = e.error || e.message || errorMessage; } catch (_) {}
        }
        throw new Error(errorMessage);
      }
      let result = { message: "Document deleted successfully!" };
      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      }
      setMsg({ type: "success", text: result.message || "Document deleted successfully!" });
      setLocalBooking((prev) => ({
        ...prev,
        documents: (prev.documents || []).filter((d) => d.filename !== filename),
      }));
      if (previewDialogOpen && previewFile?.filename === filename) handleClosePreview();
      onUpdate();
    } catch (err) {
      setMsg({ type: "error", text: "Failed to delete: " + err.message });
    }
  };

  // ── Save status/payment/notes ─────────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await axios.patch(
        `${API}/package-orders/admin/${booking.id}`,
        {
          status,
          payment_status:       payStatus,
          notes,
          transaction_id:       txnId       || undefined,
          amount_paid:          amountPaid  ? Number(amountPaid) : undefined,
          payment_date:         payDate     || undefined,
          payment_gateway:      payGateway  || undefined,
          transaction_remarks:  txnRemarks  || undefined,
        },
        { headers: getAuthHeader() }
      );
      if (res.data.success) {
        setMsg({ type: "success", text: "Changes saved successfully!" });
        setTimeout(() => { onUpdate(); onClose(); }, 800);
      } else {
        setMsg({ type: "error", text: res.data.error || "Update failed" });
      }
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.error || "Update failed" });
    }
    setSaving(false);
  };

  const InfoItem = ({ label, value }) => (
    <Box sx={{ background: "#F0F9F9", borderRadius: 2, p: "9px 12px", border: "1px solid #C8E8EB" }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#4A9FA8", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  const docs = localBooking.documents || [];

  return (
    <>
      <Modal open onClose={onClose} closeAfterTransition BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300, sx: { backdropFilter: "blur(6px)", backgroundColor: "rgba(0,0,0,0.6)" } }}>
        <Fade in>
          <Box sx={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)",
            width: fullScreen ? "95vw" : 660,
            maxHeight: "92vh", overflowY: "auto",
            background: "#fff", borderRadius: 4,
            boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
          }}>
            {/* Header */}
            <Box sx={{ background: meta.gradient || "linear-gradient(135deg,#006D77,#003D45)", p: "24px 26px 18px", position: "relative", overflow: "hidden" }}>
              <Box sx={{ position: "absolute", top: -40, right: -40, width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.09)" }} />
              <Box sx={{ position: "absolute", bottom: -30, left: -20, width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.07)" }} />
              <IconButton onClick={onClose} size="small" sx={{ position: "absolute", top: 12, right: 12, background: "rgba(255,255,255,0.2)", color: "#fff", zIndex: 2, "&:hover": { background: "rgba(255,255,255,0.35)" } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography sx={{ fontSize: 32, mb: 0.75 }}>{meta.icon || "📦"}</Typography>
                <Typography variant="h6" sx={{ color: "#fff", mb: 0.75, fontFamily: "'Sora',sans-serif" }}>{booking.package_name}</Typography>
                <Box sx={{ display: "inline-block", background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 13, fontWeight: 900, px: 1.5, py: 0.4, borderRadius: 2, fontFamily: "monospace" }}>
                  {booking.booking_ref}
                </Box>
                <Stack direction="row" gap={1} mt={1.5} flexWrap="wrap" alignItems="center">
                  <StatusChip value={booking.status} />
                  <StatusChip value={booking.payment_status} type="payment" />
                  <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>User: {booking.user_id}</Typography>
                </Stack>
              </Box>
            </Box>

            {/* Body */}
            <Box sx={{ p: { xs: "16px 16px 24px", sm: "18px 24px 26px" }, display: "flex", flexDirection: "column", gap: 2.5 }}>

              {/* Package Info */}
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#006D77", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>Package Info</Typography>
                <Grid container spacing={1}>
                  {[["MRP", `₹${booking.mrp?.toLocaleString()}`], ["Offer Price", `₹${booking.offer_price?.toLocaleString()}`], ["Savings", `₹${booking.savings?.toLocaleString()}`], ["Package ID", booking.package_id]].map(([l, v]) => (
                    <Grid item xs={6} key={l}><InfoItem label={l} value={v} /></Grid>
                  ))}
                </Grid>
              </Box>

              {/* Patient Details */}
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#006D77", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>Patient Details</Typography>
                <Grid container spacing={1}>
                  {[["Name", booking.patient_name], ["Phone", booking.phone], ["Email", booking.email], ["Age", booking.age], ["Gender", booking.gender], ["Pincode", booking.pincode]].map(([l, v]) => (
                    <Grid item xs={6} key={l}><InfoItem label={l} value={v} /></Grid>
                  ))}
                  {booking.address && <Grid item xs={12}><InfoItem label="Address" value={formatAddress(booking.address)} /></Grid>}
                </Grid>
              </Box>

              {/* Schedule */}
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#006D77", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>Schedule</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}><InfoItem label="Date"      value={booking.preferred_date} /></Grid>
                  <Grid item xs={6}><InfoItem label="Time Slot" value={booking.preferred_time} /></Grid>
                </Grid>
              </Box>
              {/* ══ TRANSACTION DETAILS ══════════════════════════════════════════════ */}
{(localBooking.payment_status === "Paid" || localBooking.razorpay_payment_id || localBooking.transaction_id) && (
  <Paper variant="outlined" sx={{ p: 2, borderColor: "#BBF7D0", background: "linear-gradient(135deg,#F0FDF4,#ECFDF5)" }}>
    <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#16A34A", textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}>
      💳 Transaction Details
      <Chip label={localBooking.payment_method === "online" ? "Online / Razorpay" : "Cash"} size="small"
        sx={{ bgcolor: localBooking.payment_method === "online" ? "#DBEAFE" : "#FEF9C3", color: localBooking.payment_method === "online" ? "#1D4ED8" : "#92400E", fontWeight: 700, fontSize: 10, ml: 0.5 }} />
    </Typography>

    {/* Read-only Razorpay fields (online payments) */}
    {localBooking.payment_method === "online" && (
      <Grid container spacing={1} mb={1.5}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ background: "#fff", borderRadius: 2, p: "9px 12px", border: "1px solid #BBF7D0" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#16A34A", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.5 }}>Razorpay Order ID</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0F172A", fontFamily: "monospace", wordBreak: "break-all" }}>
              {localBooking.razorpay_order_id || "—"}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ background: "#fff", borderRadius: 2, p: "9px 12px", border: "1px solid #BBF7D0" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#16A34A", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.5 }}>Razorpay Payment ID</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0F172A", fontFamily: "monospace", wordBreak: "break-all" }}>
              {localBooking.razorpay_payment_id || "—"}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    )}

    {/* Editable transaction fields (cash or supplement online) */}
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, mb: 1 }}>
      Manual / Override Fields
    </Typography>
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Transaction ID / UTR"
          placeholder="e.g. TXN123456789"
          value={txnId} onChange={(e) => setTxnId(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, background: "#fff" } }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Amount Paid (₹)"
          type="number" placeholder="e.g. 1999"
          value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, background: "#fff" } }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Payment Date"
          type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, background: "#fff" } }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Payment Gateway</InputLabel>
          <Select value={payGateway} label="Payment Gateway"
            onChange={(e) => setPayGateway(e.target.value)}
            sx={{ borderRadius: 2.5, background: "#fff" }}>
            {["", "Razorpay", "UPI", "Cash", "NEFT/RTGS", "Cheque", "Other"].map((g) => (
              <MenuItem key={g} value={g}>{g || "— Select —"}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Transaction Remarks"
          placeholder="e.g. Paid at collection centre"
          value={txnRemarks} onChange={(e) => setTxnRemarks(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, background: "#fff" } }} />
      </Grid>
    </Grid>

    {/* Show saved values if already present */}
    {(localBooking.transaction_id || localBooking.amount_paid || localBooking.payment_gateway) && (
      <Box sx={{ mt: 1.5, p: "8px 12px", background: "#fff", borderRadius: 2, border: "1px dashed #BBF7D0" }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#16A34A", mb: 0.5 }}>LAST SAVED</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {localBooking.transaction_id    && <Chip size="small" label={`TXN: ${localBooking.transaction_id}`}    sx={{ fontFamily: "monospace", fontSize: 10 }} />}
          {localBooking.amount_paid       && <Chip size="small" label={`₹${localBooking.amount_paid}`}           sx={{ fontSize: 10 }} />}
          {localBooking.payment_date      && <Chip size="small" label={`📅 ${localBooking.payment_date}`}        sx={{ fontSize: 10 }} />}
          {localBooking.payment_gateway   && <Chip size="small" label={localBooking.payment_gateway}             sx={{ fontSize: 10 }} />}
          {localBooking.transaction_remarks && <Chip size="small" label={localBooking.transaction_remarks}       sx={{ fontSize: 10 }} />}
        </Stack>
      </Box>
    )}
  </Paper>
)}

              {/* ══ DOCUMENTS SECTION ══════════════════════════════════════════════ */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}>
                  <FileIcon sx={{ color: "#006D77" }} />
                  Booking Documents
                  {docs.length > 0 && (
                    <Chip label={`${docs.length} File${docs.length > 1 ? "s" : ""}`} size="small"
                      sx={{ bgcolor: "#ECFDF5", color: "#059669", fontWeight: 700, ml: 1 }} />
                  )}
                </Typography>

                {/* Upload strip */}
                <Box sx={{ mb: 3, p: 2, bgcolor: "#F0F9FF", borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <UploadIcon /> Upload New Document
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <Button variant="outlined" component="label" startIcon={<UploadIcon />}
                      sx={{ borderColor: "#006D77", color: "#006D77", "&:hover": { borderColor: "#005761", bgcolor: "rgba(0,109,119,0.08)" } }}>
                      Choose File
                      <input type="file" hidden multiple accept=".png,.jpg,.jpeg,.pdf,.doc,.docx" onChange={handleFileSelect} />
                    </Button>
                    {selectedFile.length > 0 && (
                      <>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          {selectedFile.map((file, idx) => (
                            <Box key={idx} sx={{ mb: 0.5 }}>
                              <Typography variant="body2" fontWeight="600" noWrap>{file.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{(file.size / 1024 / 1024).toFixed(2)} MB</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Button variant="contained" onClick={handleUploadDoc} disabled={uploading}
                          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                          sx={{ bgcolor: "#006D77", "&:hover": { bgcolor: "#005761" }, flexShrink: 0 }}>
                          {uploading ? "Uploading…" : "Upload"}
                        </Button>
                      </>
                    )}
                  </Box>
                  {filePreviewUrl.some(Boolean) && (
                    <Box sx={{ mt: 2, textAlign: "center" }}>
                      {filePreviewUrl.map((url, idx) => url ? (
                        <img key={idx} src={url} alt="Preview"
                          style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 8, border: "2px solid #E5E7EB", margin: 6 }} />
                      ) : null)}
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Accepted: PNG, JPG, PDF, DOC, DOCX · Max 10 MB
                  </Typography>
                </Box>

                {/* Feedback */}
                {msg && (
                  <Alert severity={msg.type} sx={{ mb: 2, borderRadius: 2.5 }} onClose={() => setMsg(null)}>
                    {msg.text}
                  </Alert>
                )}

                {/* Document cards */}
                {docs.length > 0 ? (
                  <Grid container spacing={2}>
                    {docs.map((doc, idx) => {
                      // Backend fields: filename (unique), original_filename, cloudfront_url, s3_key, storage_type, uploaded_at
                      const origName   = doc.original_filename || doc.filename || `doc_${idx}`;
                      const uploadedAt = doc.uploaded_at;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Card sx={{ border: "2px solid #E5E7EB", borderRadius: 2, transition: "all 0.2s", "&:hover": { borderColor: "#006D77", boxShadow: "0 4px 12px rgba(0,109,119,0.15)" } }}>
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                                {getFileIcon(origName)}
                                <Tooltip title={origName}>
                                  <Typography variant="body2" fontWeight="600" sx={{ mt: 1, mb: 0.5 }} noWrap>
                                    {origName.length > 20 ? `${origName.substring(0, 20)}…` : origName}
                                  </Typography>
                                </Tooltip>
                                {uploadedAt && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                                    {new Date(uploadedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                  </Typography>
                                )}
                                {/* storage_type badge */}
                                <Chip label={doc.storage_type === "s3" ? "☁ Cloud" : "💾 Local"} size="small"
                                  sx={{ mb: 1.5, fontSize: 9, bgcolor: doc.storage_type === "s3" ? "#EFF6FF" : "#F0FDF4", color: doc.storage_type === "s3" ? "#2563EB" : "#16A34A" }} />
                                <Stack direction="row" spacing={1}>
                                  <IconButton size="small"
                                    sx={{ bgcolor: "#DBEAFE", "&:hover": { bgcolor: "#BFDBFE" } }}
                                    onClick={() => handlePreviewDoc(doc)} title="Preview"
                                    disabled={!doc.cloudfront_url && !doc.path && !doc.filename}>
                                    <EyeIcon fontSize="small" sx={{ color: "#2563EB" }} />
                                  </IconButton>
                                  <IconButton size="small"
                                    sx={{ bgcolor: "#D1FAE5", "&:hover": { bgcolor: "#A7F3D0" } }}
                                    onClick={() => handleDownloadDoc(doc.filename)} title="Download"
                                    disabled={!doc.filename}>
                                    <DownloadIcon fontSize="small" sx={{ color: "#059669" }} />
                                  </IconButton>
                                  <IconButton size="small"
                                    sx={{ bgcolor: "#FEE2E2", "&:hover": { bgcolor: "#FECACA" } }}
                                    onClick={() => handleDeleteDoc(doc.filename)} title="Delete"
                                    disabled={!doc.filename}>
                                    <DeleteIcon fontSize="small" sx={{ color: "#DC2626" }} />
                                  </IconButton>
                                </Stack>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: "center", py: 4, bgcolor: "#F9FAFB", borderRadius: 2 }}>
                    <FileIcon sx={{ fontSize: 64, color: "grey.300", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight="600">No documents uploaded yet</Typography>
                    <Typography variant="caption" color="text.secondary">Upload documents using the button above</Typography>
                  </Box>
                )}
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                                  <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<DownloadIcon />}
                                                    onClick={() => {
                                                      handleInvoiceDownload(booking.booking_ref)
                                                    }}
                                                    sx={{ 
                                                      bgcolor: '#006D77', 
                                                      '&:hover': { bgcolor: '#005560' },
                                                      textTransform: 'none'
                                                    }}
                                                  >
                                                    Download Invoice
                                                  </Button>
                                                </Box>
              </Paper>

              {/* Update Booking */}
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#006D77", textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5 }}>Update Booking</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Booking Status</InputLabel>
                      <Select value={status} label="Booking Status" onChange={(e) => setStatus(e.target.value)} sx={{ borderRadius: 2.5 }}>
                        {["Pending", "Confirmed", "Collected", "Completed", "Cancelled"].map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Status</InputLabel>
                      <Select value={payStatus} label="Payment Status" onChange={(e) => setPayStatus(e.target.value)} sx={{ borderRadius: 2.5 }}>
                        {["Unpaid", "Paid", "Refunded"].map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} size="small" label="Internal Notes"
                      placeholder="Add internal notes…" value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }} />
                  </Grid>
                </Grid>
                {msg && !uploading && <Alert severity={msg.type} sx={{ mt: 2, borderRadius: 2.5 }}>{msg.text}</Alert>}
                <Button fullWidth variant="contained" size="large" onClick={save} disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  sx={{ mt: 2, py: 1.5, borderRadius: 3, background: "linear-gradient(135deg,#003D45,#006D77)", fontSize: 14, fontWeight: 800, boxShadow: "0 6px 20px rgba(0,109,119,0.3)", "&:hover": { background: "linear-gradient(135deg,#004D55,#007D88)" } }}>
                  {saving ? "Saving…" : "Save Changes"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* File Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={handleClosePreview} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: "#006D77", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FileIcon />
            <Typography variant="h6">Document Preview</Typography>
          </Box>
          <IconButton onClick={handleClosePreview} sx={{ color: "white" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {previewFile && (
            <Box sx={{ py: 3 }}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Box sx={{ mb: 2 }}>{getFileIcon(previewFile.original_filename || previewFile.filename)}</Box>
                <Typography variant="h6" fontWeight="600" gutterBottom>{previewFile.original_filename || previewFile.filename}</Typography>
                {previewFile.uploaded_at && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Uploaded: {new Date(previewFile.uploaded_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </Typography>
                )}
              </Box>
              {previewContent && (
                <Box sx={{ mb: 3, textAlign: "center" }}>
                  {previewContent.isImage ? (
                    <img src={previewContent.url} alt={previewContent.filename}
                      style={{ maxWidth: "100%", maxHeight: 500, borderRadius: 8, border: "2px solid #E5E7EB" }} />
                  ) : previewContent.isPdf ? (
                    <iframe src={previewContent.url} title="PDF Preview"
                      style={{ width: "100%", height: 600, border: "2px solid #E5E7EB", borderRadius: 8 }} />
                  ) : (
                    <Paper sx={{ p: 4, bgcolor: "#F9FAFB" }}>
                      <FileIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">Preview not available for this file type.</Typography>
                      <Typography variant="caption" color="text.secondary">Please download to view.</Typography>
                    </Paper>
                  )}
                </Box>
              )}
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="contained" startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadDoc(previewFile.filename)}
                  sx={{ bgcolor: "#006D77", "&:hover": { bgcolor: "#005761" } }}>
                  Download
                </Button>
                <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
                  onClick={() => { handleClosePreview(); handleDeleteDoc(previewFile.filename); }}>
                  Delete
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Main ────────────────────────────────────────────────────────────────────────
const PackageBookings = () => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [allBookings,  setAllBookings]  = useState([]);
  const [globalStats,  setGlobalStats]  = useState({});
  const [packages,     setPackages]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [activeTab,    setActiveTab]    = useState("bookings");
  const [selectedPkg,  setSelectedPkg]  = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [search,       setSearch]       = useState("");
  const [selected,     setSelected]     = useState(null);
  const [page,         setPage]         = useState(1);
  const [viewMode,     setViewMode]     = useState("card")
const handleOpenBooking = (b) => {
  markOrderSeen('package', b.id); 
  setSelected(b);
};
  const PKG_META = useMemo(() => {
    const result = {};
    packages.forEach((pkg) => {
      result[pkg.id] = {
        label:    pkg.name,
        icon:     pkg.icon       || "💊",
        color:    pkg.badgeColor || "#006D77",
        bg:       pkg.badgeBg    || "#E6F4F5",
        gradient: pkg.gradient   || "linear-gradient(135deg,#006D77,#003D45)",
      };
    });
    return result;
  }, [packages]);



  const fetchAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const pkgRes = await axios.get(`${API}/health-camp/packages`, { headers: getAuthHeader() });
      if (pkgRes.data.success) setPackages(pkgRes.data.packages || []);
      console.log(packages)
      const res = await axios.get(`${API}/package-orders/admin/all`, { headers: getAuthHeader() });
      console.log(res.data)
      if (res.data.success) {
        setAllBookings(res.data.bookings || []);
        setGlobalStats(res.data.global_stats || {});
        registerFetchedOrders('package', (res.data.bookings || []).map(b => ({ id: b.id, status: b.status })));
      } else {
        setError(res.data.error || "Failed to load bookings");
      }
    } catch (e) {
      if (e.response?.status === 401) navigate("/login");
      else if (e.response?.status === 403) setError("Admin access required.");
      else setError("Failed to load bookings.");
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setPage(1); }, [search, filterStatus, selectedPkg]);

  const filtered = allBookings.filter((b) => {
    const matchPkg    = selectedPkg  ? b.package_id === selectedPkg : true;
    const matchStatus = filterStatus ? b.status === filterStatus    : true;
    const q           = search.toLowerCase();
    const matchSearch = q
      ? (b.patient_name?.toLowerCase().includes(q) || b.phone?.includes(q) ||
         b.booking_ref?.toLowerCase().includes(q)  || b.package_name?.toLowerCase().includes(q) ||
         b.user_id?.includes(q))
      : true;
    return matchPkg && matchStatus && matchSearch;
  });

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginated  = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

 
  const globalStatCards = [
    { label: "Total",     val: globalStats.total     || 0,                         color: "#006D77" },
    { label: "Pending",   val: globalStats.pending   || 0,                         color: "#D97706" },
    { label: "Confirmed", val: globalStats.confirmed || 0,                         color: "#007A85" },
    { label: "Completed", val: globalStats.completed || 0,                         color: "#005C65" },
    { label: "Cancelled", val: globalStats.cancelled || 0,                         color: "#DC2626" },
    // { label: "Revenue",   val: `₹${(globalStats.revenue || 0).toLocaleString()}`, color: "#003D45" },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: "100%", minHeight: "100vh", background: "#F0F4F8", overflowX: "hidden" }}>

        {/* Title bar */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: { xs: 2, sm: 3 }, pt: { xs: 2, md: 2.5 }, background: "#fff", borderBottom: "1px solid #C8E8EB" }}>
          <Box>
            <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, color: "#003D45", fontSize: { xs: 15, sm: 18, md: 20 }, lineHeight: 1.2 }}>Package Bookings</Typography>
            <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: "#4A9FA8", mt: 0.3 }}>Admin dashboard · All package bookings & analytics</Typography>
          </Box>
            <Box>
           <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              sx={{ mb: 1, mt:1 ,mr:1}}
            >
              <ToggleButton value="card">
                <ViewModuleIcon />
                Card
              </ToggleButton>
              <ToggleButton value="table">
                <ViewListIcon />
                Table
              </ToggleButton>
            </ToggleButtonGroup>
          <Button variant="outlined" onClick={fetchAll} size="small"
            sx={{ borderColor: "#C8E8EB", color: "#006D77", borderRadius: 1,mb:2, minWidth: 0, px: { xs: 1.5, sm: 2 }, "&:hover": { background: "#006D77", color: "#fff", borderColor: "#006D77" } }}>
            <RefreshIcon sx={{ fontSize: 16 }} />
            {!isMobile && "Refresh"}
          </Button>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 3 }, py: { xs: 2, md: 3 }, maxWidth: 1400 }}>

          {/* Global Stats */}
          <Grid container spacing={{ xs: 1, sm: 1.5 }} mb={3}>
            {globalStatCards.map((s) => (
              <Grid item xs={4} sm={4} md={2} key={s.label}>
                <Card sx={{ textAlign: "center", p: 0, border: "1px solid #C8E8EB" }}>
                  <CardContent sx={{ p: { xs: "10px 6px !important", sm: "16px 10px !important" } }}>
                    <Typography sx={{ color: s.color, fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: { xs: 16, sm: 20, md: 24 }, lineHeight: 1 }}>{s.val}</Typography>
                    <Typography sx={{ fontSize: { xs: 9, sm: 10 }, color: "#4A9FA8", fontWeight: 700, textTransform: "uppercase", mt: 0.5, letterSpacing: 0.3 }}>{s.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Tabs */}
          <Stack direction="row" gap={1} alignItems="center" mb={2.5} flexWrap="wrap">
            {[
              { key: "bookings", label: "All Bookings",     shortLabel: "Bookings", icon: <TableRowsIcon  sx={{ fontSize: 16 }} /> },
            ].map((t) => (
              <Button key={t.key} variant={activeTab === t.key ? "contained" : "outlined"}
                startIcon={t.icon} onClick={() => setActiveTab(t.key)} size="small"
                sx={activeTab === t.key ? { background: "linear-gradient(135deg,#003D45,#006D77)", boxShadow: "0 4px 12px rgba(0,109,119,0.3)", borderRadius: 3, fontSize: { xs: 11, sm: 13 }, px: { xs: 1.5, sm: 2 } }
                  : { borderColor: "#C8E8EB", color: "#006D77", borderRadius: 3, fontSize: { xs: 11, sm: 13 }, px: { xs: 1.5, sm: 2 }, "&:hover": { borderColor: "#006D77", background: alpha("#006D77", 0.05) } }}>
                {isMobile ? t.shortLabel : t.label}
                {t.key === "bookings" && selectedPkg && PKG_META[selectedPkg] && (
                  <Chip label={`${PKG_META[selectedPkg]?.icon} ${PKG_META[selectedPkg]?.label}`} size="small"
                    sx={{ ml: 0.75, height: 18, fontSize: 9, background: "rgba(255,255,255,0.25)", color: "inherit" }} />
                )}
              </Button>
            ))}
            {(selectedPkg || filterStatus) && (
              <Button size="small" color="error" variant="outlined"
                startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                onClick={() => {setSelectedPkg(null);setFilterStatus(null)}}
                sx={{ borderRadius: 3, ml: "auto", fontSize: { xs: 11, sm: 13 }, px: { xs: 1, sm: 2 } }}>
                {!isMobile && "Clear Filter"}
              </Button>
            )}
          </Stack>

          {/* Bookings Tab */}
          {activeTab === "bookings" && (
            <>
              <Grid container spacing={1.5} mb={2} alignItems="center">
                <Grid item xs={12} sm={5} md={5} sx={{width:600}}>
                  <TextField fullWidth size="small" placeholder="Search name, phone, booking ref…"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#4A9FA8", fontSize: 18 }} /></InputAdornment>, sx: { borderRadius: 2.5, background: "#fff" } }} />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <FormControl fullWidth size="small" sx={{width:200}}>
                    <InputLabel>Status</InputLabel>
                    <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)} sx={{ borderRadius: 2.5, background: "#fff" }}>
                      <MenuItem value="">All Statuses</MenuItem>
                      {["Pending", "Confirmed", "Collected", "Completed", "Cancelled"].map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <FormControl fullWidth size="small" sx={{width:200}}>
                    <InputLabel>Package</InputLabel>
                    <Select value={selectedPkg || ""} label="Package" onChange={(e) => setSelectedPkg(e.target.value || null)} sx={{ borderRadius: 2.5, background: "#fff" }}>
                      <MenuItem value="">All Packages</MenuItem>
                      {packages.map((pkg) => (<MenuItem key={pkg.id} value={pkg.id}>{pkg.icon} {pkg.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={1} md={1}>
                  <Chip label={`${filtered.length} results`} sx={{ background: "#E6F4F5", color: "#006D77", border: "1.5px solid #C8E8EB", fontWeight: 700, fontSize: 11 }} />
                </Grid>
              </Grid>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: "#006D77" }} /></Box>
              ) : error ? (
                <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
              ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8, color: "#94A3B8" }}>
                  <Typography sx={{ fontSize: 44, mb: 1 }}>📭</Typography>
                  <Typography fontWeight={600}>No bookings found</Typography>
                </Box>
              ) : (
                <>
                { viewMode==="table"?
                  (<TableContainer component={Paper} sx={{ border: "1px solid #C8E8EB", borderRadius: 1, overflowX: "auto", "& .MuiTable-root": { minWidth: { xs: 700, sm: 860 } } }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {["Ref", "Resident", "Package", "Date & Time", "MRP", "Offer Price", "Status", "Payment", "Docs", ""].map((h, i) => (
                            <TableCell key={i} sx={{ whiteSpace: "nowrap" }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginated.map((b) => {
                          const meta     = PKG_META[b.package_id];
                          // documents field — set by backend $push in bookings_col
                          const docCount = (b.documents || []).length;
                          const isNew = isOrderUnseen('package', b.id);
                          return (
                            // <TableRow key={b.id} onClick={() => setSelected(b)}>
                                <TableRow key={b.id} onClick={() => handleOpenBooking(b)} 
                                  sx={{ bgcolor: isNew ? "#FEF2F2" : "inherit" }}> 
                              <TableCell>
                                <Typography sx={{ fontFamily: "monospace", background: "#E6F4F5", color: "#006D77", fontSize: 12, fontWeight: 700, px: 1, py: 0.4, borderRadius: 1.5, display: "inline-block" }}>
                                  {b.booking_ref}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700, color: "#0F172A", fontSize: 13 }}>{b.patient_name}</Typography>
                                <Typography sx={{ fontSize: 11, color: "#64748B" }}>{b.phone}</Typography>
                                <Typography sx={{ fontSize: 10, color: "#CBD5E1", fontFamily: "monospace" }}>uid:{b.user_id?.slice(-6)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" alignItems="center" gap={1}>
                                  {meta && (
                                    <Avatar sx={{ width: 24, height: 24, borderRadius: 1.5, background: meta.gradient, fontSize: 13, flexShrink: 0 }}>
                                      {meta.icon}
                                    </Avatar>
                                  )}
                                  <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#0F172A" }}>{b.package_name?.replace("Ayuja ", "")}</Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0F172A" }}>📅 {b.preferred_date}</Typography>
                                <Typography sx={{ fontSize: 11, color: "#64748B" }}>⏰ {b.preferred_time}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontSize: 12, color: "#94A3B8", textDecoration: "line-through", fontWeight: 600 }}>₹{b.mrp?.toLocaleString()}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontFamily: "'Sora',sans-serif", fontSize: 13, color: "#006D77", fontWeight: 900 }}>₹{b.offer_price?.toLocaleString()}</Typography>
                              </TableCell>
                              <TableCell><StatusChip value={b.status} /></TableCell>
                              <TableCell><StatusChip value={b.payment_status} type="payment" /></TableCell>
                              <TableCell>
                                {docCount > 0 ? (
                                  <Chip icon={<FileIcon sx={{ fontSize: "14px !important" }} />} label={`${docCount}`} size="small"
                                    sx={{ bgcolor: "#ECFDF5", color: "#059669", fontWeight: 700, fontSize: 10 }} />
                                ) : (
                                  <Typography sx={{ fontSize: 11, color: "#CBD5E1" }}>—</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Tooltip title="Edit booking">
                                  <Button size="small" variant="outlined"
                                    startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                    onClick={(e) => { e.stopPropagation(); setSelected(b); }}
                                    sx={{ borderColor: "#C8E8EB", color: "#006D77", borderRadius: 2.5, fontSize: 11, whiteSpace: "nowrap", "&:hover": { background: "#006D77", color: "#fff", borderColor: "#006D77" } }}>
                                    Edit
                                  </Button>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>):
                   

                  (<Grid container spacing={2}>
                        {paginated.map((b) => {
                          const meta = PKG_META[b.package_id];
                          const docCount = (b.documents || []).length;
                          const isNew = isOrderUnseen('package', b.id); 
                          return (
                            <Grid item xs={12} sm={6} md={4} key={b.id}>
                          <Card
                              onClick={() => handleOpenBooking(b)} 
                              sx={{
                                p: 2,
                                width: 300,
                                borderRadius: 1,
                                border: isNew ? "2px solid #f8e7e7" : "1px solid #C8E8EB",
                                bgcolor: isNew ? "#FEF2F2" : "#fff", 
                                cursor: "pointer",
                                transition: "0.2s",
                                "&:hover": {
                                  boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                                  transform: "translateY(-2px)",
                                  borderColor: isNew ? "#f1b4b4" : "#006d77" 
                                },
                              }}
                              >
      
                                {/* Header */}
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography
                                    sx={{
                                      fontFamily: "monospace",
                                      background: "#E6F4F5",
                                      color: "#006D77",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      px: 1,
                                      py: 0.4,
                                      borderRadius: 1.5,
                                    }}
                                  >
                                    {b.booking_ref}
                                  </Typography>

                                  <StatusChip value={b.status} />
                                </Box>

                                {/* Patient */}
                                <Box mt={1}>
                                  <Typography fontWeight={700} fontSize={14}>
                                    {b.patient_name}
                                  </Typography>
                                  <Typography fontSize={12} color="#64748B">
                                    {b.phone}
                                  </Typography>
                                </Box>

                                {/* Package */}
                                <Box mt={1} display="flex" alignItems="center" gap={1}>
                                  {meta && (
                                    <Avatar
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: 1.5,
                                        background: meta.gradient,
                                        fontSize: 14,
                                      }}
                                    >
                                      {meta.icon}
                                    </Avatar>
                                  )}
                                  <Box>
                                    <Typography fontSize={12} fontWeight={700}>
                                      {b.package_name?.replace("Ayuja ", "")}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Date */}
                                <Box mt={1}>
                                  <Typography fontSize={12}>📅 {b.preferred_date}</Typography>
                                  <Typography fontSize={11} color="#64748B">
                                    ⏰ {b.preferred_time}
                                  </Typography>
                                </Box>

                                {/* Pricing */}
                                <Box mt={1}>
                                  <Typography
                                    fontSize={12}
                                    sx={{ textDecoration: "line-through", color: "#94A3B8" }}
                                  >
                                    ₹{b.mrp?.toLocaleString()}
                                  </Typography>
                                  <Typography
                                    fontSize={14}
                                    fontWeight={800}
                                    color="#006D77"
                                  >
                                    ₹{b.offer_price?.toLocaleString()}
                                  </Typography>
                                </Box>

                                {/* Payment */}
                                <Box mt={1}>
                                  <StatusChip value={b.payment_status} type="payment" />
                                </Box>

                                {/* Docs */}
                                <Box mt={1}>
                                  {docCount > 0 ? (
                                    <Chip
                                      icon={<FileIcon sx={{ fontSize: 14 }} />}
                                      label={`${docCount} docs`}
                                      size="small"
                                      sx={{
                                        bgcolor: "#ECFDF5",
                                        color: "#059669",
                                        fontWeight: 700,
                                        fontSize: 10,
                                      }}
                                    />
                                  ) : (
                                    <Typography fontSize={11} color="#CBD5E1">
                                      No docs
                                    </Typography>
                                  )}
                                </Box>

                                {/* Actions */}
                                <Box mt={2} display="flex" justifyContent="flex-end">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelected(b);
                                    }}
                                    sx={{
                                      borderColor: "#C8E8EB",
                                      color: "#006D77",
                                      borderRadius: 2,
                                      fontSize: 11,
                                      "&:hover": {
                                        background: "#006D77",
                                        color: "#fff",
                                      },
                                    }}
                                  >
                                    Edit
                                  </Button>
                                </Box>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid> )}  

                  {/* Pagination */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2} mb={1} flexWrap="wrap" gap={1} px={0.5}>
                    <Typography sx={{ fontSize: 12, color: "#4A9FA8", fontWeight: 600 }}>
                      Showing {((page - 1) * ROWS_PER_PAGE) + 1}–{Math.min(page * ROWS_PER_PAGE, filtered.length)} of {filtered.length} bookings
                    </Typography>
                    <Stack direction="row" gap={0.75} flexWrap="wrap">
                      <Button size="small" variant="outlined" disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                        sx={{ borderColor: "#C8E8EB", color: "#006D77", borderRadius: 2.5, fontSize: 12, px: 1.5, "&:hover": { background: "#006D77", color: "#fff", borderColor: "#006D77" } }}>
                        ← Prev
                      </Button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                        .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push("..."); acc.push(p); return acc; }, [])
                        .map((p, i) => p === "..." ? (
                          <Typography key={`e-${i}`} sx={{ alignSelf: "center", color: "#94A3B8", fontSize: 13, px: 0.5 }}>…</Typography>
                        ) : (
                          <Button key={p} size="small" variant={p === page ? "contained" : "outlined"} onClick={() => setPage(p)}
                            sx={p === page ? { background: "linear-gradient(135deg,#003D45,#006D77)", borderRadius: 2.5, minWidth: 34, fontSize: 12, boxShadow: "0 2px 8px rgba(0,109,119,0.3)" }
                              : { borderColor: "#C8E8EB", color: "#006D77", borderRadius: 2.5, minWidth: 34, fontSize: 12, "&:hover": { background: "#006D77", color: "#fff", borderColor: "#006D77" } }}>
                            {p}
                          </Button>
                        ))}
                      <Button size="small" variant="outlined" disabled={page === totalPages || totalPages === 0} onClick={() => setPage((p) => p + 1)}
                        sx={{ borderColor: "#C8E8EB", color: "#006D77", borderRadius: 2.5, fontSize: 12, px: 1.5, "&:hover": { background: "#006D77", color: "#fff", borderColor: "#006D77" } }}>
                        Next →
                      </Button>
                    </Stack>
                  </Stack>
                </>
              )}
            </>
          )}
        </Box>

        {selected && (
          <BookingDetailModal booking={selected} onClose={() => setSelected(null)} onUpdate={fetchAll} pkgMeta={PKG_META} />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default PackageBookings;